// @flow
import React from 'react';
import { connect } from 'react-redux';
import _ from 'lodash';

import { type TRound } from '../../modules/types';
import * as actions from "../../modules/actions";
import * as selectors from '../../modules/selectors';

import {
	getLeagueRoundAliases,
	getLeagueFinalFullNames,
} from '../../helpers/league';

import TwoColumnLayoutWithSidebarWidgets from "../../components/TwoColumnLayoutWithSidebarWidgets";
import JsonFetcher from '../../components/utils/JsonFetcher';
import withRoundsSelector from '../../components/utils/withRoundsSelector';
import RoundSelector from '../../components/RoundSelector';
import AdsContainer from '../../components/AdsContainer';
import Ad from '../../components/Ad';
import PageTitle from '../../components/PageTitle';
import Fixtures from '../../components/Fixtures';
import TopBar from '../../components/TopBar';
import WaiverOrderWidget from '../../components/WaiverOrderWidget';
import { isDraftLeagueByeRound } from '../../helpers/complex/leagues';

type Props = {
	selected_league_id: number,
	ladder_teams_by_id: Object,
	fetchLeague: typeof actions.leagueDraft.showDraftLeague,
	fetchLadder: typeof actions.leagueDraft.ladderDraftLeague,
	is_pending_show: boolean,
	is_pending_ladder: boolean,
	user_id: number,
	rounds_ordered: Array<Object>,
	rounds_by_id: Object,
	selected_round_id: number,
	setSelectedRoundId: Function,
	league: Object,
	has_assistant_coach: boolean,
	coach_players_by_id: Object,
	players: Object,
	rounds: Array<TRound>
}

type State = {
	rfa_order_widget_open: boolean
}

class LeagueFixturesPage extends React.Component<Props, State> {
	state = {
		rfa_order_widget_open: false
	};

	constructor(props) {
		super(props);

		_.bindAll(this, ['onToggleWaiverOrderWidget']);
	}

	componentDidMount() {
		const { selected_round_id } = this.props;

		this.fetchLeague();
		console.log(selected_round_id, 'round id');

		if (selected_round_id) {
			this.fetchLadder();
		}
	}

	componentDidUpdate(prev_props) {
		const { selected_league_id, league, selected_round_id, setSelectedRoundId } = this.props;

		if (
			selected_league_id !== prev_props.selected_league_id
		) {
			this.fetchLeague();
			this.fetchLadder();
		}
		else if (
			selected_round_id !== prev_props.selected_round_id
		) {
			this.fetchLadder();
		}

		if (
			selected_league_id !== prev_props.selected_league_id ||
			league !== prev_props.league
		) {
			setSelectedRoundId(this.league_selected_round);
		}
	}

	/**
	 * Sometimes the active round which is the selected one by default will fall outside
	 * the leagues allowed rounds, in this case we must set the selected round to one the
	 * league is playing
	 */
	get league_selected_round() {
		const { selected_round_id, league } = this.props;

		if (selected_round_id && league && this.league_rounds.length) {
			const start_round = _.first(this.league_rounds).id;
			const end_round = _.last(this.league_rounds).id;

			if ( selected_round_id < start_round) {
				return start_round;
			}
			else if (selected_round_id > end_round) {
				return end_round;
			}

			return selected_round_id;
		}

		return  null;
	}

	get league_current_round_fixtures() {
		const { league, selected_round_id } = this.props;
		return selected_round_id ?
			_.get(league, `fixture.${selected_round_id}`, []): [];
	}

	get is_league_bye_round() {
		const {
			rounds_by_id,
			league,
			selected_round_id
		} = this.props;

		const round = _.get(rounds_by_id, selected_round_id);
		return isDraftLeagueByeRound(league, round);
	}

	get league_rounds() {
		const {
			rounds_ordered,
			league
		} = this.props;

		if (rounds_ordered && league && league.start_round && league.end_round) {
			return rounds_ordered.filter(round =>
				round.id >= league.start_round && round.id <= league.end_round
			);
		}

		return rounds_ordered;
	}

	fetchLadder() {
		const { fetchLadder, selected_league_id, selected_round_id } = this.props;
		fetchLadder({
			league_id: selected_league_id,
			round: selected_round_id
		});
	}

	fetchLeague() {
		const { fetchLeague, selected_league_id } = this.props;
		fetchLeague({
			id: selected_league_id
		});
	}

	onToggleWaiverOrderWidget() {
		this.setState(state => ({
			rfa_order_widget_open: !state.rfa_order_widget_open
		}));
	}

	renderFixtures() {
		const {
			ladder_teams_by_id,
			league,
			rounds_by_id,
			has_assistant_coach,
			coach_players_by_id,
			selected_round_id,
			players,
			rounds
		} = this.props;

		return (
			<Fixtures
				league={league}
				ladder_teams_by_id={ladder_teams_by_id}
				rounds_by_id={rounds_by_id}
				rounds={rounds}
				has_assistant_coach={has_assistant_coach}
				coach_players_by_id={coach_players_by_id}
				selected_round_id={selected_round_id}
				is_league_bye_round={Boolean(this.is_league_bye_round)}
				league_current_round_fixtures={this.league_current_round_fixtures}
				players={players}
			/>
		);
	}

	renderMainSection() {
		const { league, selected_round_id, rounds_ordered } = this.props;

		const title_round =  selected_round_id || '-';
		const title_league_name = league ? league.name : '-';
		const round_full_names = getLeagueFinalFullNames(league, rounds_ordered);
		const round_full_name = round_full_names[selected_round_id];

		return (
			<div>
				<PageTitle>
					{round_full_name || `Round ${title_round}`} Fixtures for {title_league_name}
				</PageTitle>

				{this.renderFixtures()}
			</div>
		);
	}

	renderAside() {
		const { league } = this.props;

		return (
			<div>
				{league && league.id ? (
					<WaiverOrderWidget
						league_id={league.id}
						onToggle={this.onToggleWaiverOrderWidget}
						widget_open={this.state.rfa_order_widget_open}
					/>
				) : null }
			</div>
		);
	}

	render() {
		const { setSelectedRoundId, selected_round_id, league, rounds_ordered } = this.props;
		const { rfa_order_widget_open } = this.state;

		return (
			<React.Fragment>
				<JsonFetcher fetch={['rounds', 'coach_players', 'players']} />
				<TopBar league={league} />
				<RoundSelector
					selected={selected_round_id}
					rounds={this.league_rounds}
					handleClick={setSelectedRoundId}
					round_aliases={getLeagueRoundAliases(league, rounds_ordered)}
				/>

				<AdsContainer>
					<Ad
						adUnit='myleagues/fixtures'
						size='top'
					/>
				</AdsContainer>

				<TwoColumnLayoutWithSidebarWidgets sidebar_open={rfa_order_widget_open}>
					{this.renderMainSection()}
					{this.renderAside()}
				</TwoColumnLayoutWithSidebarWidgets>

				<AdsContainer>
					<Ad
						adUnit='myleagues/fixtures'
						size='bottom'
					/>
				</AdsContainer>
			</React.Fragment>
		);
	}
}

const mapStateToProps = (state, props) => {
	const selected_league_id = props.match.params.league_id;

	return {
		selected_league_id,
		league: selectors.leagueDraft.getLeague(state, props),
		is_pending_show: selectors.leagueDraft.show.isPending(state),
		is_pending_ladder: selectors.leagueDraft.ladder.isPending(state),
		ladder_teams_by_id: selectors.leagueDraft.ladder.getById(state),
		user_id: selectors.getUser(state).id,
		has_assistant_coach: Boolean(selectors.getUser(state).assistant_coach),
		coach_players_by_id: state.players.coach_by_id,
		players: selectors.players.getExtendedPlayersById(state, props),
		rounds: selectors.rounds.getRoundsWithMatches(state)
	};
};

const mapDispatchToProps = {
	fetchLeague: actions.leagueDraft.showDraftLeague,
	fetchLadder: actions.leagueDraft.ladderDraftLeague
};

export const LeagueFixtures = connect(
	mapStateToProps,
	mapDispatchToProps
)(LeagueFixturesPage);

export default withRoundsSelector(LeagueFixtures);


// WEBPACK FOOTER //
// ./src/pages/league/LeagueFixtures.js